// Okubo east (x200-255, y0-95): residential-commercial Koreatown. Quiet neon, apartment blocks with balconies, small
// groceries, laundromats and grill restaurants with hangul signs. Same 2x materials as the crossing (hd-fronts-kabuki.ts).
// Kind names are prefixed "okuboE". hd-kit's glyph table is katakana/Latin only, so the few hangul syllables live here.
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, type G, px, speckle, dither, bands, disc, frame, shadow, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { registerFront, registerRoof, registerRoofProp, type LightFn } from './hd-registry';
import { windowPane, display, door, awning, lantern, vending, plant, bandSign, bladeSign, fitS } from './hd-parts';

const hgTextW = textW, hgText = text;
/** a boxed neon band sign with hangul (same look as bandSign) */
function hangulBand(g: G, cx: number, y: number, txt: string, color: NeonColor, L: LightFn, s = 3) {
  const col = NEON[color], tw = hgTextW(txt, s), w = tw + 12, h = 6 * s + 10, x = cx - Math.floor(w / 2);
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.82));
  frame(g, x + 2, y + 2, w - 4, h - 4, col); frame(g, x + 3, y + 3, w - 6, h - 6, dk(col, 0.4));
  hgText(g, txt, x + 6, y + 5, lt(col, 0.6), s);
  L(x, y, w, h, col, 0.5, true, true);
}

// ---------------------------------------------------------------- roof materials
// waterproofing paint: concrete with a green wash, the usual rooftop coating on Okubo's apartment blocks
registerRoof('okuboEPaint', { paint(g, x, y, w, h, seed) {
  const base = mix(K.concrete, K.leaf, 0.3);
  px(g, x, y, w, h, base);
  for (let j = 0; j < h; j += 48) for (let i = 0; i < w; i += 64) {
    const v = hash2(x + i + seed, y + j);
    if (v < 0.3) px(g, x + i, y + j, Math.min(64, w - i), Math.min(48, h - j), mix(base, K.concreteLo, 0.45));
    px(g, x + i, y + j, Math.min(64, w - i), 1, mix(base, K.concreteLo, 0.7)); px(g, x + i, y + j, 1, Math.min(48, h - j), mix(base, K.concreteLo, 0.7));
  }
  speckle(g, x, y, w, h, K.concreteLo, 0.03, 41, 2);
  for (let k = 0; k < Math.floor(w * h / 15000); k++) { // a stairwell-door shadow strip and a puddle
    const px0 = x + 24 + Math.floor(hash2(seed, k + 9) * Math.max(1, w - 60)), py0 = y + 20 + Math.floor(hash2(k + 9, seed) * Math.max(1, h - 40));
    disc(g, px0, py0, 9, 3, mix(base, K.glass, 0.35));
  }
} });

// ---------------------------------------------------------------- roof props
registerRoofProp('okuboEWash', ({ g, x, y }) => { // a clothesline between two poles, laundry in palette colors
  px(g, x, y, 3, 22, K.ink); px(g, x + 61, y, 3, 22, K.ink); shadow(g, x + 6, y + 22, 58, 4, 0.3);
  px(g, x + 3, y + 2, 58, 1, K.metalLo);
  const tones = [K.white, K.pink, K.cyan, K.warmHi, K.plaster, K.white];
  for (let i = 0; i < 6; i++) { const cx = x + 6 + i * 9, c = tones[i], len = 10 + Math.floor(hash2(x + i, y) * 6); px(g, cx, y + 3, 7, len, dk(c, 0.2)); px(g, cx, y + 3, 7, 2, lt(c, 0.2)); px(g, cx + 5, y + 3, 2, len, dk(c, 0.45)); px(g, cx + 1, y + 2, 1, 2, K.ink); px(g, cx + 5, y + 2, 1, 2, K.ink); }
});
registerRoofProp('okuboEJars', ({ g, x, y }) => { // onggi: a row of lidded earthenware crocks
  shadow(g, x + 3, y + 12, 52, 5, 0.35);
  for (let i = 0; i < 4; i++) {
    const cx = x + 8 + i * 13, r = i % 2 ? 6 : 5;
    disc(g, cx, y + 8, r, 7, K.ink); disc(g, cx - 1, y + 7, r - 1, 6, K.woodHi); disc(g, cx + 1, y + 9, r - 2, 4, K.wood);
    px(g, cx - r + 1, y, r * 2 - 2, 3, K.ink); px(g, cx - r + 2, y + 1, r * 2 - 4, 1, K.woodLo); px(g, cx - 2, y + 4, 2, 5, lt(K.woodHi, 0.3));
  }
});
registerRoofProp('okuboESolar', ({ g, x, y, L }) => { // a rooftop solar water heater panel
  shadow(g, x + 4, y + 6, 44, 28, 0.35);
  px(g, x, y, 44, 28, K.ink); px(g, x + 2, y + 2, 40, 24, K.glassLo);
  for (let i = 0; i < 40; i += 8) px(g, x + 2 + i, y + 2, 1, 24, K.glass);
  for (let j = 0; j < 24; j += 8) px(g, x + 2, y + 2 + j, 40, 1, K.glass);
  px(g, x + 3, y + 3, 14, 2, K.glassHi); dither(g, x + 2, y + 16, 40, 10, alpha(K.cyan, 0.2));
  px(g, x + 2, y + 26, 40, 2, K.metalLo); px(g, x + 6, y + 28, 3, 4, K.metalLo); px(g, x + 35, y + 28, 3, 4, K.metalLo);
  L(x + 2, y + 2, 40, 24, K.cyan, 0.08);
});
registerRoofProp('okuboEGarden', ({ g, x, y }) => { // planter boxes with chives and chilli
  shadow(g, x + 3, y + 10, 46, 5, 0.3);
  for (let i = 0; i < 2; i++) {
    const bx = x + i * 26;
    px(g, bx, y + 8, 22, 9, K.ink); px(g, bx + 1, y + 9, 20, 7, K.woodLo); px(g, bx + 1, y + 9, 20, 1, K.woodHi);
    for (let k = 0; k < 6; k++) { const hx = bx + 2 + k * 3, hh = 3 + Math.floor(hash2(hx, y) * 5); px(g, hx, y + 8 - hh, 2, hh, k % 3 === 2 ? K.leafHi : K.leaf); }
    px(g, bx + 8, y + 3, 2, 2, K.red); px(g, bx + 15, y + 4, 2, 2, K.red);
  }
});

// ---------------------------------------------------------------- fronts
/** balcony rails over the upper windows of an apartment block (windows start at x+10, 26 apart, y+14) */
function balconies(g: G, x: number, y: number, w: number, upper: number) {
  if (upper < 26) return;
  const wh = Math.min(18, upper - 8);
  for (let i = x + 10; i + 16 <= x + w - 8; i += 26) {
    px(g, i - 4, y + 14 + wh + 3, 24, 5, K.ink); px(g, i - 3, y + 14 + wh + 4, 22, 1, K.metalHi); px(g, i - 3, y + 14 + wh + 6, 22, 1, K.metalLo);
    for (let r = i - 3; r < i + 20; r += 4) px(g, r, y + 14 + wh + 4, 1, 3, K.metalLo);
    if (hash2(i, y) < 0.35) { px(g, i, y + 14 + wh - 1, 14, 8, alpha(K.white, 0.55)); px(g, i + 1, y + 14 + wh, 12, 1, alpha(K.pink, 0.5)); } // laundry draped on the rail
  }
}
registerFront('okuboEFlats', { wall: mix(K.plasterLo, K.slate, 0.25), wallAc: true }, ({ g, b, x, y, w, h, gf, gy, upper, L }) => {
  balconies(g, x, y, w, upper);
  px(g, x + 6, gy - 4, w - 12, 3, alpha(K.night, 0.4));
  // glass lobby door in the middle
  door(g, x + Math.floor(w / 2) - 22, gy + 6, 44, gf - 8, K.amber, L);
  px(g, x + Math.floor(w / 2) - 26, gy - 2, 52, 4, K.ink); px(g, x + Math.floor(w / 2) - 25, gy - 1, 50, 2, K.concreteHi);
  // mailbox banks on both sides
  for (const mx of [x + Math.floor(w / 2) - 80, x + Math.floor(w / 2) + 46]) {
    px(g, mx - 1, gy + 10, 36, 22, K.ink);
    for (let j = 0; j < 2; j++) for (let i = 0; i < 4; i++) { px(g, mx + i * 9, gy + 11 + j * 10, 8, 9, K.metalHi); px(g, mx + i * 9, gy + 11 + j * 10, 8, 1, lt(K.metalHi, 0.3)); px(g, mx + 2 + i * 9, gy + 15 + j * 10, 4, 1, K.metalLo); }
  }
  // a bicycle leaning at the wall and a plant
  const bxp = x + 14 + Math.floor(hash2(b.x, b.y) * 3) * 4;
  disc(g, bxp + 6, gy + gf - 8, 6, 6, K.ink); disc(g, bxp + 6, gy + gf - 8, 4, 4, K.walk); disc(g, bxp + 26, gy + gf - 8, 6, 6, K.ink); disc(g, bxp + 26, gy + gf - 8, 4, 4, K.walk);
  px(g, bxp + 6, gy + gf - 16, 20, 2, K.pink); px(g, bxp + 14, gy + gf - 16, 2, 8, K.pink); px(g, bxp + 24, gy + gf - 22, 2, 8, K.metalHi); px(g, bxp + 22, gy + gf - 23, 6, 2, K.ink);
  plant(g, x + w - 34, gy + gf - 20);
});
registerFront('okuboEGrocer', { wall: mix(K.wallHi, K.leaf, 0.18), upper: 'none' }, ({ g, x, y, w, h, gf, gy, upper, L }) => {
  hangulBand(g, x + Math.floor(w / 2), y + 12 + Math.max(0, upper - 34), '마트', 'amber', L, 3);
  awning(g, x + 8, gy - 2, w - 16, 10, K.leafHi, K.leaf);
  display(g, x + 12, gy + 14, Math.min(100, w - 150), gf - 20, K.amber, L);
  door(g, x + Math.min(100, w - 150) + 30, gy + 14, 46, gf - 16, K.white, L);
  vending(g, x + w - 60, gy + gf - 40, dk(K.pink, 0.5), L);
  // produce crates out on the pavement edge
  const cols = [K.amber, K.pink, K.leafHi, K.warmHi, K.amber];
  for (let i = 0; i < 4; i++) {
    const cx = x + 20 + i * 30 + Math.min(100, w - 150) + 70;
    if (cx + 24 > x + w - 66) break;
    px(g, cx, gy + gf - 12, 24, 10, K.ink); px(g, cx + 1, gy + gf - 11, 22, 8, K.woodHi); px(g, cx + 1, gy + gf - 7, 22, 1, K.woodLo);
    for (let k = 0; k < 5; k++) disc(g, cx + 4 + k * 4, gy + gf - 14, 2, 2, cols[(i + k) % cols.length]);
  }
  px(g, x + 8, gy + gf - 3, w - 16, 2, alpha(K.night, 0.4));
});
registerFront('okuboELaundry', { wall: mix(K.wall, K.cyan, 0.18), upper: 'none' }, ({ g, x, y, w, h, gf, gy, upper, L }) => {
  hangulBand(g, x + Math.floor(w / 2), y + 12 + Math.max(0, upper - 34), '세탁', 'cyan', L, 3);
  bandSign(g, x + 40, y + 16 + Math.max(0, upper - 34), '24H', 'cyan', L, 1);
  // a long lit window with a row of round washer doors
  const wx = x + 12, ww = Math.min(w - 90, 240), wy = gy + 6, wh = gf - 14;
  px(g, wx - 2, wy - 2, ww + 4, wh + 4, K.ink); bands(g, wx, wy, ww, wh, lt(K.cyan, 0.55), dk(K.cyan, 0.3), 4);
  for (let i = 0; i + 30 <= ww; i += 30) {
    const cx = wx + 16 + i, cy = wy + wh - 16;
    disc(g, cx, cy, 11, 11, K.ink); disc(g, cx, cy, 9, 9, K.metalHi); disc(g, cx, cy, 7, 7, K.glassLo); disc(g, cx - 1, cy + 1, 5, 5, K.glass); px(g, cx - 4, cy - 3, 4, 2, K.glassHi);
  }
  for (let k = 0; k < wh; k++) px(g, wx + 150 + Math.floor(k / 2), wy + k, 2, 1, alpha(K.white, 0.16));
  L(wx, wy, ww, wh, K.cyan, 0.4, true);
  door(g, wx + ww + 16, gy + 6, 40, gf - 8, K.cyan, L);
  px(g, x + w - 34, gy + gf - 16, 24, 4, K.ink); px(g, x + w - 33, gy + gf - 15, 22, 2, K.woodHi); px(g, x + w - 32, gy + gf - 12, 3, 10, K.ink); px(g, x + w - 14, gy + gf - 12, 3, 10, K.ink); // bench
});
// A two-tile parts-and-tyre annex beside the Exhaust Shrine courtyard: a stacked-tyre column, a muffler hung out as the
// shop sign, an OPEN side door and a pink neon stripe.
registerFront('okuboETyre', { wall: mix(K.wall, K.pink, 0.12), upper: 'none' }, ({ g, x, y, w, gf, gy, L }) => {
  px(g, x + 4, y + 12, w - 8, 5, K.ink); px(g, x + 5, y + 13, w - 10, 3, K.pink); px(g, x + 5, y + 13, w - 10, 1, lt(K.pink, 0.5));
  L(x + 4, y + 11, w - 8, 7, K.pink, 0.5, true, true);
  // hanging muffler: chain, mounting plate, silver can with a pink band and a dark outlet pipe
  const mx = x + 14, my = y + 22;
  px(g, mx + 7, y + 18, 1, 5, K.metalLo); px(g, mx + 2, my - 1, 12, 2, K.ink);
  px(g, mx, my + 1, 16, 26, K.ink); px(g, mx + 1, my + 2, 14, 24, K.metalHi); px(g, mx + 1, my + 2, 4, 24, lt(K.metalHi, 0.3)); px(g, mx + 11, my + 2, 4, 24, K.metalLo);
  px(g, mx + 1, my + 10, 14, 4, K.pink); px(g, mx + 1, my + 10, 14, 1, lt(K.pink, 0.4));
  px(g, mx + 5, my + 27, 6, 7, K.ink); px(g, mx + 6, my + 28, 4, 5, K.metalLo); L(mx, my, 16, 28, K.pink, 0.12, false);
  // OPEN plate over the door
  bandSign(g, x + w - 24, y + 24, 'OPEN', 'amber', L, 1);
  // stacked tyres on the left, a roll leaning in front
  for (let i = 0; i < 5; i++) {
    const ty = gy + gf - 10 - i * 8, off = i % 2 ? 2 : 0;
    px(g, x + 6 + off, ty, 26, 9, K.ink); px(g, x + 7 + off, ty + 1, 24, 7, K.tarHi); px(g, x + 7 + off, ty + 1, 24, 2, K.metalLo);
    px(g, x + 14 + off, ty + 2, 10, 4, K.ink); px(g, x + 15 + off, ty + 3, 8, 2, K.tar);
  }
  disc(g, x + 20, gy + gf - 8, 9, 9, K.ink); disc(g, x + 20, gy + gf - 8, 7, 7, K.tarHi); disc(g, x + 20, gy + gf - 8, 3, 3, K.ink);
  door(g, x + w - 30, gy + 8, 24, gf - 10, K.amber, L);
  px(g, x + w - 32, gy + 6, 28, 3, K.ink); px(g, x + w - 31, gy + 7, 26, 1, K.metalHi);
  px(g, x + 3, gy + gf - 3, w - 6, 2, alpha(K.night, 0.4));
});
registerFront('okuboEBbq', { wall: mix(K.wallHi, K.pink, 0.16), upper: 'none' }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  hangulBand(g, x + Math.floor(w / 2), y + 12 + Math.max(0, upper - 34), '식당', 'amber', L, 3);
  if (sign) bladeSign(g, x + w - 38, y + 10, sign.text, sign.color, L, 2);
  // an extractor duct climbing the wall, grease-dark
  px(g, x + 14, y + 2, 8, gy - y + 6, K.ink); px(g, x + 15, y + 2, 3, gy - y + 6, K.metalHi); px(g, x + 18, y + 2, 3, gy - y + 6, K.metalLo);
  for (let j = 10; j < gy - y; j += 22) px(g, x + 13, y + j, 10, 3, K.metalLo);
  // eave and a string of red paper lanterns
  px(g, x + 28, gy - 6, w - 100, 6, K.kawara); px(g, x + 28, gy - 6, w - 100, 1, K.kawaraHi); px(g, x + 28, gy, w - 100, 3, alpha(K.night, 0.5));
  for (let i = 0; i < 5 && x + 44 + i * 40 < x + w - 90; i++) lantern(g, x + 44 + i * 40, gy + 2, i % 2 ? K.amber : K.red, L);
  // big window onto grill tables: a smoky glow with table silhouettes
  const wx = x + 34, ww = w - 130, wy = gy + 18, wh = gf - 24;
  px(g, wx - 2, wy - 2, ww + 4, wh + 4, K.ink); bands(g, wx, wy, ww, wh, K.warmHi, K.warm, 3);
  for (let i = 8; i + 22 < ww; i += 38) { px(g, wx + i, wy + wh - 12, 22, 4, K.woodLo); px(g, wx + i + 8, wy + wh - 8, 4, 8, K.woodLo); disc(g, wx + i + 11, wy + wh - 15, 4, 3, K.red); disc(g, wx + i + 25, wy + wh - 20, 4, 6, K.ink); }
  dither(g, wx, wy, ww, 10, alpha(K.white, 0.28));
  L(wx, wy, ww, wh, K.amber, 0.4, true);
  door(g, x + w - 82, gy + 14, 52, gf - 16, K.amber, L);
  for (let i = 0; i < 3; i++) { px(g, x + w - 82 + i * 18, gy + 14, 17, 14, K.redLo); px(g, x + w - 82 + i * 18, gy + 14, 17, 2, K.redDk); }
});
